import $ from 'jquery'
import Backbone from 'backbone'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import icon from 'scoreboard/components/icon'
import DatasetsFilterEquationForm from './datasets-filter-equation-form'
import appDisplay from 'scoreboard/components/app-display'
import updateTypeOptions from './update-type-options'
import dropdown from 'scoreboard/components/dropdown'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import numericValidator from 'scoreboard/components/validators/numeric-validator'
import numericEvaluator from 'scoreboard/components/evaluators/numeric-evaluator'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import DateInput from 'scoreboard/components/date-input'
import TimeInput from 'scoreboard/components/time-input'
import DataType from 'scoreboard/datasets/model/data-type'
const { DATE, TIME, DATE_TIME } = DataType

var ValueOrEquation = Backbone.View.extend({

	/**
	 * Handles a manual/calculated updateType dropdown.  When the update type is
	 * manual, the corresponding input box is visible.  When the update type is
	 * calculated, a set equation button is visible instead.  The controls
	 * are found using the following selectors:
	 *
	 * .update-type
	 * .set-equation
	 * .value-container input
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model the dataset
	 *
	 * @param {Object} options.field the field being filtered on
	 *
	 * @param {String} options.dataType the data type expected for the result
	 * of the equation
	 *
	 * @param {String} [options.equation] an already-defined equation to display
	 *
	 * @param {String} [options.updateType='manual'] an already-defined
	 * updateType used to initialize the form
	 *
	 * @param {String} [options.value] an already-defined
	 * value used to initialize the form
	 *
	 * @param {FormView} options.formView the FormView
	 *
	 * @param {Boolean} [options.allowIncomplete=false] whether the value or
	 * equation is allowed to be blank (e.g. dataset filters in dashboards)
	 *
	 * @param {boolean} [options.allowCalculated=true] whether to allow
	 * calculated values
	 *
	 * @param {Object|Array} [options.validator] one or more validators to append
	 * to the list of validations
	 *
	 * @param {Boolean} [options.timeDisplayType] how to format a time value
	 *
	 * @param {Number} [options.movePercentPlacesBy] passed as marked field
	 * parameter by the same name
	 *
	 * @param {String} [options.currencyCode] used for the display of currency
	 * code by marked fields
	 */
	initialize: function(options = {}) {
		this.options = options
		this.formView = this.options.formView
		this.allowCalculated = _.isNil(this.options.allowCalculated) ? true :
			this.options.allowCalculated
	},

	events: {
		'change .update-type': 'changeUpdateType',
		'click .set-equation': 'equation'
	},

	/** @type {Dataset} */
	model: null,

	render: function() {
		let els = ['.update-type', '.set-equation', '.value-container input']
		_.each(els, this.verifyDom.bind(this))

		// render updateType
		let updateType = this.options.updateType || 'manual'
		dropdown.idLabels(this.$('.update-type').empty(), updateTypeOptions, updateType)
		this.$('.update-type').toggleClass('is-not-displayed', !this.allowCalculated)

		// render equation
		let equation = this.options.equation
		this.$('.set-equation')
			.val(equation)
			.html(this.buttonText(equation))

		// render value
		let input = this.$('.value-container input')
		input.val(this.options.value)
		if (this.options.dataType === DATE) {
			this.markedField = new DateInput({
				el: input,
				required: !this.options.allowIncomplete
			}).render()
		} else if (_.includes([TIME, DATE_TIME], this.options.dataType)) {
			this.markedField = new TimeInput({
				el: input,
				required: !this.options.allowIncomplete,
				hasCalendarControl: this.options.dataType === DATE_TIME,
				timeDisplayType: this.options.timeDisplayType,
				isDateTime: this.options.dataType === DATE_TIME
			}).render()
		} else if (isNumericDataType(this.options.dataType) ||
			// score is always a numeric datatype
			this.options.dataType === 'score') {
			let validator = this.options.allowIncomplete
				? [numericValidator]
				: [requiredValidator, numericValidator]
			if (this.options.validator) {
				validator = validator.concat(_.isArray(this.options.validator) ?
					this.options.validator : [this.options.validator])
			}
			this.markedField = new MarkedField({
				el: input,
				dataType: this.options.dataType,
				evaluator: numericEvaluator,
				movePercentPlacesBy: this.options.movePercentPlacesBy,
				validator,
				currencyCode: this.options.currencyCode
			}).render()
		} else {
			this.markedField = new MarkedField({
				el: input,
				validator: this.options.allowIncomplete ? null : requiredValidator
			}).render()
		}

		this.changeUpdateType()
		return this
	},

	verifyDom: function(selector) {
		if (this.$(selector).length == 0) {
			throw Error(`Couldn't find '${selector}' dropdown`)
		}
	},

	buttonText: function(text) {
		let buttonText = _.isEmpty(text) ? i18n.get('scorecards.node.metric.equation.set') : text
		return _.escape(buttonText) + icon('edit')
	},

	/**
	 * Ensures the correct field are displayed based on the current update
	 * type
	 */
	changeUpdateType: function(e) {
		// when changing update type, clear any errors on the corresponding value input
		_.invoke(this.markedField, 'clearError')
		// toggle visibility of the value input and the equation button
		let updateType = this.$('.update-type').val()
		this.$('.value-container').toggleClass('is-not-displayed', updateType === 'calculated')
		this.$('.set-equation').toggleClass('is-not-displayed', updateType === 'manual')
	},

	/**
	 * Displays a dialog where it's possible to edit the equation.
	 */
	equation: function(e) {
		let equation = this.$('.set-equation').val()
		let form = new DatasetsFilterEquationForm({
			model: this.model,
			field: this.options.field,
			equation,
			dataType: this.options.dataType,
			title: i18n.get('scorecards.node.metric.equation.set'),
			required: true // the equation is required
		})
		appDisplay.dialog(form)
		form.on('done', (equation) => {
			this.formView.change()
			this.formView.clearErrors()
			this.$('.set-equation').val(equation).html(this.buttonText(equation))
			this.trigger('equationDialog:close')
		})

		this.trigger('equationDialog:open')
	},

	test: function() {
		let data = {
			updateType: this.$('.update-type').val(),
			equation: this.$('.set-equation').val()
		}
		// see if there are any calculated series without equations
		var error = data.updateType === 'calculated' && _.isEmpty(data.equation)
		if (error) {
			return i18n.get('datasets.filters.equation.required')
		}
	},

	/**
	 * Cleans up listeners related to this form, but doesn't remove this form's
	 * DOM element
	 */
	cleanup: function() {
		this.undelegateEvents()
		_.invoke(this.markedField, 'clearError')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.markedField], 'remove')
	}

})


/**
 * Renders a pair of ValueOrEquation views inside another view.
 * @param {Object} filter the filter being edited
 * @param {Object} field the field the filter applies to
 * @param {Dataset} model the dataset
 * @param {FormView} formView the formView
 * @param {jQuery} the element containing the two ValueOrEquation views
 * @param {String} [dataType] the dataType of the value. If unspecified,
 * read from the field
 * @param {boolean} [allowCalculated=true] whether to allow
 * calculated values
 * @param {Object|Array} [validator] one or more validators to append
 * to the list of validations.
 * @param {String} [selector='.value-or-equation'] How to find the elements to
 * render on.
 * @param {String} [timeDisplayType] format for times
 * inputs
 * @param {Number} [movePercentPlacesBy] allows customization of
 * ValueOrEquation parameter by the same name
 * @param {String} [currencyCode] currency code for marked fields to use
 * @return {ValueOrEquation[]} an array containing two ValueOrEquation views
 */
function renderValueOrEquations({
	filter, model, formView, field, el, dataType,
	allowCalculated=true, validator, selector='.value-or-equation',
	timeDisplayType, movePercentPlacesBy, currencyCode
}) {
	el = $(el)
	dataType = _.isUndefined(dataType) ?
		model && _.get(model.getUnderlyingField(field), 'dataType') :
		dataType
	const options = { model, field, formView, filter, dataType, validator }
	const getValue = (ordinal) => {
		switch (dataType) {
			case 'date': return filter[`date${ordinal}`]
			case 'time': return filter[`time${ordinal}`]
			case 'date-time': return filter[`dateTime${ordinal}`]
			default: return filter[`value${ordinal}`]
		}
	}

	// #39054 If the data type we're making editors for is not the data type of
	// the filter, then it is intended to be edited in other inputs - not these.
	const filterIsCompatibleDataType = isCompatibleDataType(dataType, filter)
	const fieldIsCompatibleDataType = isCompatibleDataType(dataType, field)
	const [equation1, equation2] = filterIsCompatibleDataType || fieldIsCompatibleDataType ?
		[filter.equation1, filter.equation2] : [undefined, undefined]

	let valueOrEquation1 = new ValueOrEquation(_.extend({
		allowCalculated,
		el: el.find(selector).eq(0),
		updateType: filter.updateType1,
		equation: equation1,
		value: getValue(1),
		dataType,
		timeDisplayType,
		movePercentPlacesBy,
		currencyCode
	}, options)).render()
	let valueOrEquation2 = new ValueOrEquation(_.extend({
		allowCalculated,
		el: el.find(selector).eq(1),
		updateType: filter.updateType2,
		equation: equation2,
		value: getValue(2),
		dataType,
		timeDisplayType,
		movePercentPlacesBy,
		currencyCode
	}, options)).render()
	return [valueOrEquation1, valueOrEquation2]
}


/**
 * @param dataType {string}
 * @param filterOrField {DatasetFilter | DatasetField | {dataType: string}}
 */
function isCompatibleDataType(dataType, filterOrField) {
	const dataTypeOrFilterFieldNull = _.isNil(dataType) || _.isNil(filterOrField)
	const bothDataTypesNumeric = isNumericDataType(dataType) && isNumericDataType(filterOrField)
	if (dataTypeOrFilterFieldNull || bothDataTypesNumeric) return true

	return dataType === filterOrField.dataType
}

export default ValueOrEquation
export { renderValueOrEquations }

ValueOrEquation.__filename__ = 'datasets/filters/value-or-equation.js'
